import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './StreamContainer.module.css';
import { FlvPlayer } from '../../components/FlvPlayer';
import { useSocket } from '../../contexts/SocketContext';
import {
  SOCKET_URL,
  STAGE_PLAYER_ENDED,
  STAGE_PUBLISHER_STARTED,
  STAGE_PUBLISHER_STOPPED,
  STAGE_RELOAD,
  STAGE_RELOAD_OVERLAY,
  STREAM_ENDED,
} from '../../consts/socket';
interface IOverlayMessage {
  elisaType: 'reload' | string;
  timestamp: number;
  payload: any;
}

function StreamContainer() {
  const [searchParams] = useSearchParams();
  const overlayUrl = searchParams.get('overlayUrl') || '';

  const campaignId = searchParams.get('campaignId') || '';
  const destinationId = searchParams.get('did' || '');
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isPreRecorded = !!parseInt(searchParams.get('pr') || '0');
  const liveCatching = !!parseInt(searchParams.get('lc') || '0');
  const liveCatchingJumpThreshold = parseFloat(
    searchParams.get('lc_jt') || '0',
  );
  const liveCatchingMaxLatency = parseFloat(searchParams.get('lc_max') || '0');
  const liveCatchingMinRemain = parseFloat(searchParams.get('lc_min') || '0');
  const sendVideoUpdatesToOverlay = overlayUrl && isPreRecorded;
  const screensaverActive = useRef<boolean>(false);
  const screensaverTimeoutIdByDeviceIdRef = useRef<Map<string, NodeJS.Timeout>>(
    new Map(),
  );
  const srcLink = searchParams.get('srcLink') || '';
  const useWorker = !!parseInt(searchParams.get('uw') || '0');
  const logPartialMessages = !!parseInt(searchParams.get('debug') || '0');
  const orientation =
    (searchParams.get('o') || 'p') === 'p' ? 'portrait' : 'landscape';
  const showCursor = !!parseInt(searchParams.get('cursor') || '0');

  const inferSrcLinkType = useCallback(() => {
    if (!srcLink) {
      return;
    }

    const urlObj = new URL(srcLink);
    const path = urlObj.pathname;

    if (path.indexOf('.') === -1) {
      return;
    }

    return path.split('.').pop()?.toLowerCase();
  }, [srcLink]);

  const srcLinkType =
    searchParams.get('srcLinkType') || inferSrcLinkType() || 'flv';

  const { connect, disconnect, subscribe, emit } = useSocket();

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    connect({
      url: SOCKET_URL,
      query: { campaignId, userUid: 'studio' },
      connectCallback: addSocketEventHandlers,
    });

    return () => {
      window.removeEventListener('message', handleMessage);
      disconnect();
    };
  }, []);

  const postMessageToOverlay = (elisaType: string, payload?: any) => {
    if (!iframeRef.current) {
      return;
    }

    const timestamp = new Date().getTime();

    const message = JSON.stringify({
      elisaType,
      timestamp,
      payload,
    });

    const loggableWithPayload = !['video-time-update'].includes(elisaType);

    if (loggableWithPayload) {
      console.log(`Overlay Message Sending: ${message}`);
    } else if (logPartialMessages) {
      console.log(`
        Overlay Message Sending (PARTIAL): ${JSON.stringify({
          elisaType,
          timestamp,
        })}`);
    }

    iframeRef.current.contentWindow?.postMessage(JSON.parse(message), '*');
  };

  const handleMessage = (e: MessageEvent<IOverlayMessage>) => {
    const origin = e.origin;
    const urlObj = new URL(origin);
    const hostname = urlObj.hostname;

    if (
      hostname !== 'elisa-overlay.onrender.com' &&
      !hostname.includes('localhost') &&
      !hostname.includes('ngrok')
    ) {
      return;
    }

    console.log(`Overlay Message Received: ${JSON.stringify(e.data)}`);

    switch (e.data.elisaType) {
      case 'after-show-end':
        console.log('AFTER SHOW END FROM MESSAGE EVENT');
        emit(STREAM_ENDED, { forceEnd: true });
        break;
      case 'reload':
        console.log('RELOAD FROM MESSAGE EVENT');
        window.location.reload();
        break;
    }
  };

  const addSocketEventHandlers = () => {
    subscribe(STAGE_PUBLISHER_STARTED, (data: { deviceId: string }) => {
      const deviceId = data?.deviceId || '0';
      console.log(
        `Socket Event Received: ${STAGE_PUBLISHER_STARTED} for device ${deviceId}`,
      );

      if (screensaverActive.current) {
        console.log(`RELOAD AS STREAM RESUMED FOR DEVICE ${deviceId}.`);
        window.location.reload();
        return;
      }

      if (screensaverTimeoutIdByDeviceIdRef.current.has(deviceId)) {
        clearTimeout(screensaverTimeoutIdByDeviceIdRef.current.get(deviceId));
        screensaverTimeoutIdByDeviceIdRef.current.delete(deviceId);
        console.log(
          `RELOAD AS STREAM RESUMED WHILE SCREENSAVER WAS SCHEDULED FOR DEVICE ${deviceId}.`,
        );
        window.location.reload();
        return;
      }
    });

    subscribe(STAGE_PUBLISHER_STOPPED, (data: { deviceId: string }) => {
      const deviceId = data?.deviceId || '0';
      console.log(
        `Socket Event Received: ${STAGE_PUBLISHER_STOPPED} for device ${deviceId}`,
      );

      if (screensaverTimeoutIdByDeviceIdRef.current.has(deviceId)) {
        console.log(
          `Screensaver message already scheduled for device ${deviceId}.`,
        );
        return;
      }

      const timeoutId = setTimeout(() => {
        postMessageToOverlay('screensaver_on', { orientation, deviceId });
        screensaverActive.current = true;
      }, 3000);

      screensaverTimeoutIdByDeviceIdRef.current.set(deviceId, timeoutId);

      console.log('Screensaver message scheduled in 3s.');
    });

    subscribe(STAGE_RELOAD, () => {
      console.log(`Socket Event Received: ${STAGE_RELOAD}`);
      console.log('RELOAD FROM SOCKET EVENT');
      window.location.reload();
    });

    subscribe(STAGE_RELOAD_OVERLAY, () => {
      console.log(`Socket Event Received: ${STAGE_RELOAD_OVERLAY}`);
      console.log('RELOAD OVERLAY ONLY FROM SOCKET EVENT');
      iframeRef.current?.contentWindow?.location?.reload();
    });

    console.log(`Socket Event Subscriptions Made`);
  };

  const onEnded = useCallback(async () => {
    if (sendVideoUpdatesToOverlay) {
      postMessageToOverlay('video-ended');
    }

    emit(STAGE_PLAYER_ENDED, { destinationId });

    isPreRecorded && emit(STREAM_ENDED);
  }, []);

  const onTimeUpdated = useCallback(async (time: number) => {
    if (sendVideoUpdatesToOverlay) {
      postMessageToOverlay('video-time-update', { time });
    }
  }, []);

  return (
    <div className={styles.wrapper}>
      {srcLink && (
        <FlvPlayer
          isPreRecorded={isPreRecorded}
          liveCatching={liveCatching}
          liveCatchingJumpThreshold={liveCatchingJumpThreshold}
          liveCatchingMaxLatency={liveCatchingMaxLatency}
          liveCatchingMinRemain={liveCatchingMinRemain}
          onVideoEnded={onEnded}
          onVideoTimeUpdated={onTimeUpdated}
          src={srcLink}
          srcType={srcLinkType}
          useWorker={useWorker}
        />
      )}
      {overlayUrl && (
        <iframe
          ref={iframeRef}
          title="video-overlay"
          src={overlayUrl}
          className={styles.iframe}
        />
      )}
      {!showCursor && <div className={styles.nonCursor} />}
    </div>
  );
}

export default StreamContainer;
